import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS, readJson, writeJson } from '../lib/storage';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const ORDER: ThemeChoice[] = ['light', 'dark', 'system'];

function systemPrefersDark(): boolean {
  // `matchMedia` is missing in some test environments and older webviews.
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

function resolve(choice: ThemeChoice): ResolvedTheme {
  if (choice === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return choice;
}

/**
 * Dark mode, as a class on `<html>`.
 *
 * Three states rather than two: `system` is the default and keeps following the
 * OS, and only an explicit light/dark choice is persisted. A two-state toggle
 * silently pins whatever the OS happened to be on first visit, which then never
 * follows the user's evening switch to dark.
 */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(() => {
    const stored = readJson<string>(STORAGE_KEYS.theme, 'system');
    return ORDER.includes(stored as ThemeChoice) ? (stored as ThemeChoice) : 'system';
  });
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(choice));

  // Apply to the document and keep following the OS while the choice is
  // `system`. The listener is removed on any change, so it never leaks.
  useEffect(() => {
    const apply = () => {
      const next = resolve(choice);
      setResolved(next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      document.documentElement.style.colorScheme = next;
    };
    apply();

    if (choice !== 'system' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [choice]);

  const setTheme = useCallback((next: ThemeChoice) => {
    setChoice(next);
    writeJson(STORAGE_KEYS.theme, next);
  }, []);

  /** Cycles light → dark → system, so the toggle can reach all three states. */
  const cycleTheme = useCallback(() => {
    setChoice((current) => {
      const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length] ?? 'system';
      writeJson(STORAGE_KEYS.theme, next);
      return next;
    });
  }, []);

  return { theme: choice, resolvedTheme: resolved, setTheme, cycleTheme };
}
